// Picking a chart-of-accounts code. Used wherever a module stores one — posting groups, loan
// types, asset classes, bank accounts.
//
// Typed into a text box, an account code is a guess: nobody remembers whether interest income is
// 4300 or 4310, and a typo is only discovered when a posting fails days later. The chart is right
// there, so offer it.
import { type Account, useApi } from "@keel/core";
import { Select } from "./Select";
import { Field } from "./ui";

export interface AccountSelectProps {
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** `asset` · `liability` · `equity` · `income` · `expense`. Other types are shown but disabled. */
  want?: Account["type"];
  /** Allow no account at all, with this label. */
  emptyLabel?: string;
  /** Control accounts are kept by a subledger; nothing else may post to them. */
  allowControl?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. Accounts of the wrong type stay in the list, greyed out: a chart where the one
 * you expected is simply missing sends people off to read it in another tab.
 */
export function AccountSelect({
  value,
  onChange,
  want,
  emptyLabel,
  allowControl = false,
  disabled,
  placeholder = "Choose an account…",
}: AccountSelectProps) {
  const accounts = useApi<Account[]>("/accounting/accounts").data ?? [];
  const usable = accounts.filter((a) => a.isActive !== false && (allowControl || !a.isControl));
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {usable.map((a) => (
        <option key={a.code} value={a.code} disabled={!!want && a.type !== want}>
          {a.code} · {a.name}
          {want && a.type !== want ? ` (${a.type})` : ""}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and a hint that says when the chosen account is the wrong type. */
export function AccountField({
  label,
  hint,
  ...rest
}: AccountSelectProps & { label: string; hint?: string }) {
  const accounts = useApi<Account[]>("/accounting/accounts").data ?? [];
  const chosen = accounts.find((a) => a.code === rest.value);
  const wrong = chosen && rest.want && chosen.type !== rest.want;
  return (
    <Field label={label} hint={wrong ? `${chosen.code} is a ${chosen.type} account` : hint}>
      <AccountSelect {...rest} />
    </Field>
  );
}
